import React, { useCallback, useMemo, memo, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Grid2 as Grid,
  TextField,
  Box,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { RootState, useAppDispatch, useAppSelector } from '@/app/store';
import TimeSlotSelector from '@/shared/ui/TimeSlotSelector';
import { DateRangePicker, handleSnackbar } from '@/shared/ui';
import { useQueryErrorSnackbar } from '@/shared/hooks';
import type { BasicDetailsProps } from '@/entities/campaign';
import { isOpsRole } from '@/shared/constants';
import { CITY_GROUPS } from '@/shared/utils/cityGroups';
import { setAdvertiserBasic, setRemoveCityGroupModal } from '../campaignsSlice';
import {
  creativesAPI,
  setAddCreativeModal,
  useCreativeFolderNamesQuery,
} from '@/features/dooh';
import DaypartingTimeparting from './DaypartingTimeparting';

// Memoized selectors
const selectCampaignType = (state: RootState) =>
  state.dooh.campaigns.campaignDetails.campaignType;

// City Group Selection Component
const CityGroupSelection = memo(
  ({
    selectedCityGroups,
    onCityGroupChange,
    cityGroups,
    error,
    helperText,
  }: {
    selectedCityGroups: string[];
    onCityGroupChange: (cityGroupId: string) => void;
    cityGroups: typeof CITY_GROUPS;
    error?: boolean;
    helperText?: string;
  }) => (
    <Grid size={{ xs: 12 }} mb={2}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Select State
      </Typography>
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mb: 2, display: 'block' }}
        >
          {helperText || 'Please select at least one state to continue'}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {cityGroups
          .filter(g => !g.disabled)
          .map(cityGroup => {
            const isSelected = selectedCityGroups.includes(cityGroup.id);
            return (
              <Box
                key={cityGroup.id}
                onClick={() => onCityGroupChange(cityGroup.id)}
                sx={{
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : '#e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  width: 200,
                  height: 200,
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: isSelected ? 'primary.dark' : 'primary.light',
                    boxShadow: 2,
                  },
                }}
              >
                <Box
                  component="img"
                  src={cityGroup.image}
                  alt={cityGroup.name}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1,
                    objectFit: 'contain',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: 'text.primary',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {cityGroup.displayName}
                </Typography>
              </Box>
            );
          })}
      </Box>
    </Grid>
  )
);

CityGroupSelection.displayName = 'CityGroupSelection';

// Brand Folder Selector — Autocomplete with "Add New" option
const ADD_NEW_OPTION = { id: 'addNew', name: 'Create New Brand Folder' };

const BrandFolderSelector = memo(
  ({ formik, disabled }: { formik: any; disabled?: boolean }) => {
    const dispatch = useAppDispatch();
    const {
      data,
      isLoading,
      error: creativesError,
    } = useCreativeFolderNamesQuery();

    useQueryErrorSnackbar(creativesError, 'Failed to load brand folders');
    const folders = useMemo(() => data ?? [], [data]);

    const selectedBrand = formik.values.selectedBrand || null;

    // Track folder IDs before "Add New" is clicked so we can detect the new one
    const prevFolderIdsRef = useRef<Set<string>>(new Set());
    const pendingAutoSelectRef = useRef(false);

    // Auto-select newly created brand folder after the modal closes and query refreshes
    useEffect(() => {
      if (!pendingAutoSelectRef.current) return;
      const newFolder = folders.find(
        folder => !prevFolderIdsRef.current.has(folder.id)
      );
      if (newFolder) {
        pendingAutoSelectRef.current = false;
        void (async () => {
          try {
            const folder = await creativesAPI.getCreativeFolderById(
              newFolder.id
            );
            formik.setField('selectedBrand', folder);
            dispatch(
              setAdvertiserBasic({ field: 'selectedBrand', value: folder })
            );
          } catch (error) {
            dispatch(
              handleSnackbar({
                open: true,
                message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to load the new brand folder.',
                severity: 'error',
                autoHideDuration: 3000,
              })
            );
          }
        })();
      }
    }, [folders, dispatch, formik.setField]);

    const handleChange = useCallback(
      async (_: any, value: any) => {
        if (value?.id === 'addNew') {
          // Snapshot current folder IDs so we can detect the new one after creation
          prevFolderIdsRef.current = new Set(folders.map(folder => folder.id));
          pendingAutoSelectRef.current = true;
          dispatch(setAddCreativeModal({ open: true }));
          return;
        }
        if (!value) {
          formik.setField('selectedBrand', null);
          dispatch(setAdvertiserBasic({ field: 'selectedBrand', value: null }));
          return;
        }
        try {
          const folder = await creativesAPI.getCreativeFolderById(value.id);
          formik.setField('selectedBrand', folder);
          dispatch(
            setAdvertiserBasic({ field: 'selectedBrand', value: folder })
          );
        } catch (error) {
          dispatch(
            handleSnackbar({
              open: true,
              message:
                error instanceof Error
                  ? error.message
                  : 'Failed to load the selected brand folder.',
              severity: 'error',
              autoHideDuration: 3000,
            })
          );
        }
      },
      [dispatch, formik.setField, folders]
    );

    return (
      <Autocomplete
        options={[ADD_NEW_OPTION, ...folders]}
        getOptionLabel={(opt: any) => opt?.name ?? ''}
        value={selectedBrand}
        onChange={handleChange}
        loading={isLoading}
        disabled={disabled}
        isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
        filterOptions={(options, state) => {
          const query = state.inputValue.toLowerCase();
          const matchingFolders = options.filter(
            opt => opt.id !== 'addNew' && opt.name.toLowerCase().includes(query)
          );

          const hasMatches = matchingFolders.length > 0;

          if (hasMatches) {
            return [ADD_NEW_OPTION, ...matchingFolders];
          }

          return [ADD_NEW_OPTION, { id: 'noOptions', name: 'No options' }];
        }}
        renderOption={(props, option: any) => {
          if (option.id === 'addNew') {
            return (
              <li
                {...props}
                key="addNew"
                style={{ fontWeight: 600, color: '#1976d2' }}
              >
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                {option.name}
              </li>
            );
          }
          if (option.id === 'noOptions') {
            return (
              <li
                {...props}
                key="noOptions"
                style={{
                  color: 'gray',
                  pointerEvents: 'none',
                  fontStyle: 'italic',
                }}
              >
                No folder found
              </li>
            );
          }
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Creative Brand Folder"
            required
            error={
              formik.touched.selectedBrand &&
              Boolean(formik.errors.selectedBrand)
            }
            helperText={
              formik.touched.selectedBrand &&
              (formik.errors.selectedBrand as string)
            }
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
      />
    );
  }
);

const BasicDetails: React.FC<BasicDetailsProps> = memo(({ formik }) => {
  const dispatch = useAppDispatch();
  const isEditMode = formik.isEditMode ?? false;
  const campaignType = useAppSelector(selectCampaignType) || '';
  const userRole =
    useAppSelector((state: RootState) => state.auth?.user?.role) || '';
  const isOps = isOpsRole(userRole);

  const originalStartDate = useAppSelector(
    (state: RootState) => state.dooh.campaigns.campaignDetails.campaignStartDate
  );

  const originalStartDateRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode && originalStartDate && !originalStartDateRef.current) {
      originalStartDateRef.current = originalStartDate;
    }
  }, [isEditMode, originalStartDate]);

  const handleDateRangeChange = useCallback(
    (startDate: string | null, endDate: string | null) => {
      formik.setField('campaignStartDate', startDate ?? '');
      formik.setField('campaignEndDate', endDate ?? '');
    },
    [formik.setField]
  );

  const prefilledCityGroupsRef = useRef<string[]>([]);

  useEffect(() => {
    if (isEditMode && prefilledCityGroupsRef.current.length === 0) {
      prefilledCityGroupsRef.current = formik.values.selectedCityGroup || [];
    }
  }, [isEditMode]);
  const availableFilters = useAppSelector(
    (state: RootState) => state.dooh.campaigns.campaignDetails.availableStates
  );

  const dynamicCityGroups = useMemo(() => {
    if (!availableFilters || availableFilters.length === 0) {
      return CITY_GROUPS.map(group => ({ ...group, disabled: true }));
    }

    const cityFilters = availableFilters.find(
      (f: any) => f.key === 'address.city'
    );
    const stateFilters = availableFilters.find(
      (f: any) => f.key === 'address.state'
    );

    const availableCities = new Set(
      cityFilters?.values
        ?.filter((v: any) => v.count > 0)
        .map((v: any) => v.key.toUpperCase()) || []
    );
    const availableStates = new Set(
      stateFilters?.values
        ?.filter((v: any) => v.count > 0)
        .map((v: any) => v.key.toUpperCase()) || []
    );

    const NCR_CITIES = new Set([
      'DELHI',
      'GURUGRAM',
      'NOIDA',
      'GHAZIABAD',
      'GREATER NOIDA',
      'GURGAON',
    ]);
    const NCR_STATES = new Set(['DELHI', 'HARYANA']);

    const nonNcrAvailableCities = new Set(
      [...availableCities].filter(
        (c): c is string => !NCR_CITIES.has(c as string)
      )
    );

    return CITY_GROUPS.map(group => {
      const isNcrGroup = group.id === 'delhi-ncr';

      if (isNcrGroup) {
        const hasNcrCity = group.cities.some(c =>
          availableCities.has(c.toUpperCase())
        );
        return { ...group, disabled: !hasNcrCity };
      }

      if (group.states.every(s => NCR_STATES.has(s.toUpperCase()))) {
        return { ...group, disabled: true };
      }

      const isStateAvailable = group.states.some(s =>
        availableStates.has(s.toUpperCase())
      );
      const isCityAvailable = group.cities.some(c =>
        nonNcrAvailableCities.has(c.toUpperCase())
      );

      return {
        ...group,
        disabled: !(isStateAvailable && isCityAvailable),
      };
    });
  }, [availableFilters]);

  // Memoized form values
  const selectedCityGroups = useMemo(
    () => formik.values.selectedCityGroup || [],
    [formik.values.selectedCityGroup]
  );

  // Handler for city group selection
  const handleCityGroupChange = useCallback(
    (cityGroupId: string) => {
      const current: string[] = formik.values.selectedCityGroup || [];
      const isDeselecting = current.includes(cityGroupId);
      const isPrefilled = prefilledCityGroupsRef.current.includes(cityGroupId);

      const shouldShowModal =
        isDeselecting && (isEditMode ? isPrefilled : true);

      if (shouldShowModal) {
        dispatch(setRemoveCityGroupModal({ open: true, cityGroupId }));
        return;
      }

      const updated = isDeselecting
        ? current.filter(id => id !== cityGroupId)
        : [...current, cityGroupId];

      formik.setField('selectedCityGroup', updated);
      dispatch(
        setAdvertiserBasic({ field: 'selectedCityGroup', value: updated })
      );
    },
    [dispatch, formik.setField, formik.values.selectedCityGroup, isEditMode]
  );

  return (
    <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2, width: '80%', mb: 4 }}>
      <Typography variant="h6" gutterBottom mb={2}>
        Basic Details
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Campaign name"
            name="campaignName"
            value={formik.values.name || ''}
            onChange={e => formik.setField('name', e.target.value)}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            disabled={isEditMode}
            required
          />
        </Grid>

        {/* Brand Folder */}
        <Grid size={{ xs: 12, md: 6 }}>
          <BrandFolderSelector formik={formik} disabled={isEditMode} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Industry"
            placeholder="Enter Industry"
            value={formik.values.industry || ''}
            onChange={e => formik.setField('industry', e.target.value)}
            disabled={isEditMode}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Client/Agency"
            placeholder="Enter Client/Agency Name"
            value={formik.values.agency || ''}
            onChange={e => formik.setField('agency', e.target.value)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={isOps ? 'Revenue' : 'Budget'}
            placeholder={isOps ? 'Enter Revenue' : 'Enter Budget'}
            type="number"
            value={formik.values.campaignBudget || ''}
            onChange={e => formik.setField('campaignBudget', e.target.value)}
            error={
              formik.touched.campaignBudget &&
              Boolean(formik.errors.campaignBudget)
            }
            helperText={
              formik.touched.campaignBudget && formik.errors.campaignBudget
            }
            required
          />
        </Grid>

        <Grid size={{ xs: 12 }} mb={2}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Date & Time
          </Typography>

          <Grid container spacing={3}>
            {/* Date Range Picker */}
            <Grid size={{ xs: 12, md: 6 }}>
              <DateRangePicker
                label="Campaign Date Range"
                startDate={formik.values.campaignStartDate}
                endDate={formik.values.campaignEndDate}
                onDateRangeChange={handleDateRangeChange}
                includeTime={true}
                error={
                  (formik.touched.campaignStartDate &&
                    Boolean(formik.errors.campaignStartDate)) ||
                  (formik.touched.campaignEndDate &&
                    Boolean(formik.errors.campaignEndDate))
                }
                helperText={
                  (formik.touched.campaignStartDate &&
                    formik.errors.campaignStartDate) ||
                  (formik.touched.campaignEndDate &&
                    formik.errors.campaignEndDate)
                }
                minDate={dayjs().format('YYYY-MM-DD')}
                lockedStartDate={
                  isEditMode &&
                  originalStartDateRef.current &&
                  !dayjs(originalStartDateRef.current).isAfter(dayjs(), 'day')
                    ? originalStartDateRef.current
                    : undefined
                }
                calendarProps={{
                  enforceMinDays: false,
                }}
                required
              />
            </Grid>
          </Grid>
        </Grid>

        {campaignType === 'TIME_CAMPAIGN' && (
          <Grid size={{ xs: 12 }}>
            <TimeSlotSelector setField={formik.setField} />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <DaypartingTimeparting formik={formik} />
        </Grid>

        {/* City Group Selection */}
        <CityGroupSelection
          selectedCityGroups={selectedCityGroups}
          onCityGroupChange={handleCityGroupChange}
          cityGroups={dynamicCityGroups}
          error={
            formik.touched.selectedCityGroup &&
            Boolean(formik.errors.selectedCityGroup)
          }
          helperText={
            formik.touched.selectedCityGroup
              ? (formik.errors.selectedCityGroup as string)
              : ''
          }
        />
      </Grid>
    </Paper>
  );
});

BasicDetails.displayName = 'BasicDetails';

export default BasicDetails;
